export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  status: string;
  /** True when an identity provider owns the display name, so the account
   * cannot change it from its own settings. */
  name_managed_externally?: boolean;
}

export interface AuthSession {
  session_id: string;
  user_id: string;
  email: string;
  expires_at: string;
  stepup_verified_at?: string;
  recovery_required: boolean;
}

export interface SessionTokenResponse {
  id: string;
  user_id: string;
  access_token: string;
  refresh_token: string;
  expires_at: string;
  refresh_expires_at: string;
  recovery_required: boolean;
}

export interface StepUpResponse {
  stepup_verified_at: string;
}

/** How a person can confirm a privileged act: the code from their
 * authenticator app, or signing in again with single sign-on. */
export type StepUpMethod = "totp" | "sso";

export interface AuthMeResponse {
  user: AuthUser;
  session: AuthSession;
  feature_flags?: Record<string, boolean>;
  /** Per-user authorization facts (e.g. quality_governance) — unlike
   * feature_flags, these differ between callers. */
  capabilities?: Record<string, boolean>;
  /** Absent from a server that predates single sign-on step-up. */
  step_up_methods?: StepUpMethod[];
}

export interface AuthPolicy {
  registration_open: boolean;
}

export interface PasswordChallenge {
  totp_challenge_token: string;
  recovery_challenge_token: string;
  expires_at: string;
}

export interface PasswordSessionResponse {
  session: SessionTokenResponse;
  expires_at: string;
}

export type PasswordLoginResponse = PasswordChallenge | PasswordSessionResponse;

export function isPasswordSessionResponse(
  response: PasswordLoginResponse,
): response is PasswordSessionResponse {
  return "session" in response;
}

export interface PasskeyChallenge {
  challenge_token: string;
  options: unknown;
  expires_at: string;
}

export interface Passkey {
  id: string;
  nickname?: string;
  transports: string[];
  backed_up: boolean;
  last_used_at?: string;
  created_at: string;
}

export interface PasskeyRegistrationResult extends Passkey {
  recovery_codes?: string[];
  recovery_required_cleared?: boolean;
}

export interface TOTPEnrollment {
  secret: string;
  uri: string;
  account_name: string;
  issuer: string;
}

export interface SetupInspectResponse {
  user: AuthUser;
  purpose: string;
  state: string;
  expires_at: string;
  /** The workspace the link belongs to; blank when it has no name yet. */
  tenant_name?: string;
}

export interface SetupBeginResponse {
  user: AuthUser;
  purpose: string;
  expires_at: string;
  totp_enrollment: TOTPEnrollment;
  totp_qr_code_data_url: string;
}

export interface SetupCompleteResponse {
  user: AuthUser;
  session: SessionTokenResponse;
  recovery_codes: string[];
}

/** How a passkey is added from a signed-in session. */
export interface EnrollPasskeyOptions {
  /** Another recovery code, for a recovering session that is no longer
   * fresh. */
  recoveryCode?: string;
  /** Called when the browser is about to show the device's prompt, after
   * Starling has handed over the one-time challenge. */
  onDevicePrompt?: () => void;
}

export class AuthAPIError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    code: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "AuthAPIError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface AuthClient {
  policy(): Promise<AuthPolicy>;
  me(accessToken: string): Promise<AuthMeResponse>;
  loginPasskey(): Promise<SessionTokenResponse>;
  loginPassword(
    email: string,
    password: string,
  ): Promise<PasswordLoginResponse>;
  loginTOTP(
    challengeToken: string,
    code: string,
  ): Promise<SessionTokenResponse>;
  loginRecovery(
    challengeToken: string,
    code: string,
  ): Promise<SessionTokenResponse>;
  refresh(refreshToken: string): Promise<SessionTokenResponse>;
  stepUp(
    accessToken: string,
    input: { method: "totp"; totp: string },
  ): Promise<StepUpResponse>;
  exchangeSSOCode(exchangeCode: string): Promise<SessionTokenResponse>;
  startSSOStepUp(accessToken: string): Promise<{ redirect_url: string }>;
  logout(accessToken: string): Promise<void>;
  logoutAll(accessToken: string): Promise<void>;
  /** A recovering session that is no longer fresh confirms the passkey that
   * finishes recovery with one of its recovery codes. */
  beginPasskeyRegistration(
    accessToken: string,
    recoveryCode?: string,
  ): Promise<PasskeyChallenge>;
  finishPasskeyRegistration(
    accessToken: string,
    challengeToken: string,
    credential: unknown,
    nickname?: string,
  ): Promise<PasskeyRegistrationResult>;
  listPasskeys(accessToken: string): Promise<Passkey[]>;
  renamePasskey(
    accessToken: string,
    id: string,
    nickname: string,
  ): Promise<Passkey>;
  deletePasskey(accessToken: string, id: string): Promise<void>;
}

export interface AccountSetupClient {
  inspect(token: string): Promise<SetupInspectResponse>;
  begin(token: string, password: string): Promise<SetupBeginResponse>;
  complete(
    token: string,
    password: string,
    code: string,
  ): Promise<SetupCompleteResponse>;
}

const defaultAPIBaseURL = "/api";
const jsonHeaders = { "Content-Type": "application/json" };

export function resolveAuthAPIURL(
  path: string,
  baseURL = import.meta.env.VITE_API_BASE_URL,
) {
  const apiBaseURL = baseURL || defaultAPIBaseURL;
  const cleanBaseURL = apiBaseURL.replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBaseURL}${cleanPath}`;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  accessToken?: string,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", jsonHeaders["Content-Type"]);
  }
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }
  const response = await fetch(resolveAuthAPIURL(path), { ...init, headers });
  if (response.status === 204) {
    return undefined as T;
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = body?.error;
    throw new AuthAPIError(
      error?.message ?? "Authentication request failed",
      response.status,
      error?.code ?? "AUTH_REQUEST_FAILED",
      isRecord(error?.details) ? error.details : {},
    );
  }
  return body as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isWebAuthnAvailable() {
  return (
    typeof window !== "undefined" &&
    "PublicKeyCredential" in window &&
    !!navigator.credentials
  );
}

export const authClient: AuthClient = {
  policy: () => apiFetch<AuthPolicy>("/v1/auth/policy"),
  me: (accessToken) => apiFetch<AuthMeResponse>("/v1/auth/me", {}, accessToken),
  loginPassword: (email, password) =>
    apiFetch<PasswordChallenge>("/v1/auth/login/password", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  loginTOTP: (challengeToken, code) =>
    apiFetch<SessionTokenResponse>("/v1/auth/login/totp", {
      method: "POST",
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    }),
  loginRecovery: (challengeToken, code) =>
    apiFetch<SessionTokenResponse>("/v1/auth/login/recovery", {
      method: "POST",
      body: JSON.stringify({ challenge_token: challengeToken, code }),
    }),
  refresh: (refreshToken) =>
    apiFetch<SessionTokenResponse>("/v1/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
  exchangeSSOCode: (exchangeCode) =>
    apiFetch<SessionTokenResponse>("/v1/auth/sso/exchange", {
      method: "POST",
      body: JSON.stringify({ exchange_code: exchangeCode }),
    }),
  startSSOStepUp: (accessToken) =>
    apiFetch<{ redirect_url: string }>(
      "/v1/auth/sso/stepup/start",
      { method: "POST" },
      accessToken,
    ),
  stepUp: (accessToken, input) =>
    apiFetch<StepUpResponse>(
      "/v1/auth/stepup",
      {
        method: "POST",
        body: JSON.stringify(input),
      },
      accessToken,
    ),
  logout: (accessToken) =>
    apiFetch<void>("/v1/auth/logout", { method: "POST" }, accessToken),
  logoutAll: (accessToken) =>
    apiFetch<void>("/v1/auth/logout/all", { method: "POST" }, accessToken),
  beginPasskeyRegistration: (accessToken, recoveryCode) =>
    apiFetch<PasskeyChallenge>(
      "/v1/auth/passkey/register/begin",
      recoveryCode
        ? {
            method: "POST",
            body: JSON.stringify({ recovery_code: recoveryCode }),
          }
        : { method: "POST" },
      accessToken,
    ),
  finishPasskeyRegistration: (
    accessToken,
    challengeToken,
    credential,
    nickname,
  ) =>
    apiFetch<PasskeyRegistrationResult>(
      "/v1/auth/passkey/register/finish",
      {
        method: "POST",
        body: JSON.stringify({
          challenge_token: challengeToken,
          credential,
          nickname: nickname?.trim() || undefined,
        }),
      },
      accessToken,
    ),
  listPasskeys: async (accessToken) => {
    const response = await apiFetch<{ passkeys: Passkey[] }>(
      "/v1/auth/passkeys",
      {},
      accessToken,
    );
    return response.passkeys;
  },
  renamePasskey: (accessToken, id, nickname) =>
    apiFetch<Passkey>(
      `/v1/auth/passkeys/${id}`,
      { method: "PATCH", body: JSON.stringify({ nickname }) },
      accessToken,
    ),
  deletePasskey: (accessToken, id) =>
    apiFetch<void>(
      `/v1/auth/passkeys/${id}`,
      { method: "DELETE" },
      accessToken,
    ),
  loginPasskey: async () => {
    if (!isWebAuthnAvailable()) {
      throw new AuthAPIError(
        "Passkeys are not available in this browser",
        400,
        "PASSKEY_UNAVAILABLE",
      );
    }
    const challenge = await apiFetch<PasskeyChallenge>(
      "/v1/auth/login/passkey/begin",
      {
        method: "POST",
      },
    );
    const publicKey = publicKeyRequestOptions(challenge.options);
    const credential = await runCeremony(() =>
      navigator.credentials.get({ publicKey }),
    );
    return apiFetch<SessionTokenResponse>("/v1/auth/login/passkey/finish", {
      method: "POST",
      body: JSON.stringify({
        challenge_token: challenge.challenge_token,
        credential: publicKeyCredentialToJSON(
          credential as PublicKeyCredential,
        ),
      }),
    });
  },
};

export async function createPasskeyCredential(options: unknown) {
  if (!isWebAuthnAvailable()) {
    throw new AuthAPIError(
      "Passkeys are not available in this browser",
      400,
      "PASSKEY_UNAVAILABLE",
    );
  }
  const publicKey = publicKeyCreationOptions(options);
  const credential = await runCeremony(() =>
    navigator.credentials.create({ publicKey }),
  );
  return publicKeyCredentialToJSON(credential as PublicKeyCredential);
}

/** The code each browser ceremony exception is reported under. */
export const CEREMONY_FAILURE_CODES: Readonly<Record<string, string>> = {
  NotAllowedError: "PASSKEY_CANCELLED",
  AbortError: "PASSKEY_CANCELLED",
  InvalidStateError: "PASSKEY_ALREADY_ON_DEVICE",
  SecurityError: "PASSKEY_WRONG_ADDRESS",
  NotSupportedError: "PASSKEY_UNAVAILABLE",
};

/** Runs a browser passkey ceremony and reports how it stopped as an
 * AuthAPIError code, so callers never show the browser's own message. */
async function runCeremony(
  ceremony: () => Promise<Credential | null>,
): Promise<Credential> {
  let credential: Credential | null;
  try {
    credential = await ceremony();
  } catch (err) {
    if (err instanceof DOMException) {
      throw new AuthAPIError(
        err.message,
        400,
        CEREMONY_FAILURE_CODES[err.name] ?? "PASSKEY_CEREMONY_FAILED",
      );
    }
    throw err;
  }
  if (!credential) {
    throw new AuthAPIError(
      "Passkey ceremony was cancelled",
      400,
      "PASSKEY_CANCELLED",
    );
  }
  return credential;
}

/** A relying party that doesn't cover this page makes the browser refuse
 * with a message about WebAuthn internals; this says it before asking. */
function assertRelyingPartyCoversPage(rpID: unknown) {
  if (typeof rpID !== "string" || rpID === "") return;
  const host = window.location.hostname.toLowerCase();
  const id = rpID.toLowerCase();
  if (host === id || host.endsWith(`.${id}`)) return;
  throw new AuthAPIError(
    `Passkeys here are bound to ${rpID}, not ${host}`,
    400,
    "PASSKEY_WRONG_ADDRESS",
  );
}

function publicKeyRequestOptions(
  options: unknown,
): PublicKeyCredentialRequestOptions {
  const publicKey = clonePublicKey(options);
  if (typeof publicKey.challenge !== "string") {
    throw new AuthAPIError(
      "Invalid passkey challenge options",
      400,
      "PASSKEY_OPTIONS_INVALID",
    );
  }
  assertRelyingPartyCoversPage(publicKey.rpId);
  publicKey.challenge = base64URLToBuffer(publicKey.challenge);
  publicKey.allowCredentials = (publicKey.allowCredentials ?? []).map(
    (credential) => ({
      ...credential,
      id: base64URLToBuffer(credential.id as string),
    }),
  );
  return publicKey as unknown as PublicKeyCredentialRequestOptions;
}

function publicKeyCreationOptions(
  options: unknown,
): PublicKeyCredentialCreationOptions {
  const publicKey = clonePublicKey(options);
  if (
    typeof publicKey.challenge !== "string" ||
    typeof publicKey.user?.id !== "string"
  ) {
    throw new AuthAPIError(
      "Invalid passkey creation options",
      400,
      "PASSKEY_OPTIONS_INVALID",
    );
  }
  assertRelyingPartyCoversPage(publicKey.rp?.id);
  publicKey.challenge = base64URLToBuffer(publicKey.challenge);
  publicKey.user = {
    ...publicKey.user,
    id: base64URLToBuffer(publicKey.user.id),
  };
  publicKey.excludeCredentials = (publicKey.excludeCredentials ?? []).map(
    (credential) => ({
      ...credential,
      id: base64URLToBuffer(credential.id as string),
    }),
  );
  return publicKey as unknown as PublicKeyCredentialCreationOptions;
}

type PublicKeyOptionsRecord = Record<string, unknown> & {
  challenge?: unknown;
  allowCredentials?: Array<Record<string, unknown>>;
  excludeCredentials?: Array<Record<string, unknown>>;
  user?: Record<string, unknown> & { id?: unknown };
  rp?: Record<string, unknown> & { id?: unknown };
  rpId?: unknown;
};

function clonePublicKey(options: unknown): PublicKeyOptionsRecord {
  const value = options as { publicKey?: PublicKeyOptionsRecord };
  return structuredClone(value.publicKey ?? value);
}

function publicKeyCredentialToJSON(credential: PublicKeyCredential) {
  const response = credential.response as AuthenticatorAssertionResponse &
    AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    rawId: bufferToBase64URL(credential.rawId),
    type: credential.type,
    authenticatorAttachment: credential.authenticatorAttachment,
    response: {
      clientDataJSON: bufferToBase64URL(response.clientDataJSON),
      authenticatorData: response.authenticatorData
        ? bufferToBase64URL(response.authenticatorData)
        : undefined,
      signature: response.signature
        ? bufferToBase64URL(response.signature)
        : undefined,
      userHandle: response.userHandle
        ? bufferToBase64URL(response.userHandle)
        : undefined,
      attestationObject: response.attestationObject
        ? bufferToBase64URL(response.attestationObject)
        : undefined,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
  };
}

function base64URLToBuffer(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(
    normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "="),
  );
  return Uint8Array.from(binary, (char) => char.charCodeAt(0)).buffer;
}

function bufferToBase64URL(buffer: ArrayBuffer | null) {
  if (!buffer) return null;
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}
